import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ShoppingCart, Plus, Minus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = ({ cart, updateCart }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Product not found');
      navigate('/shop');
    }
  };

  const addToCart = () => {
    if (!product || quantity <= 0) return;
    
    if (quantity > product.inventory) {
      toast.error('Not enough inventory');
      return;
    }

    const existingItem = cart.find(item => item.id === product.id);
    let newCart;
    
    if (existingItem) {
      newCart = cart.map(item => 
        item.id === product.id 
          ? { ...item, quantity: item.quantity + quantity }
          : item
      );
    } else {
      newCart = [...cart, { ...product, quantity }];
    }
    
    updateCart(newCart);
    toast.success(`Added ${quantity} ${product.name} to cart`);
  };

  if (!product) {
    return <div className="pt-24 px-6 text-center">Loading...</div>;
  }

  return (
    <div className="pt-32 pb-16 px-6 md:px-12 lg:px-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto"
      >
        <div className="grid md:grid-cols-2 gap-12">
          {/* Images */}
          <div>
            {product.images && product.images.length > 0 ? (
              <>
                <div className="border border-white/10 overflow-hidden mb-4" data-testid="main-product-image">
                  <img 
                    src={product.images[selectedImage]} 
                    alt={product.name}
                    className="w-full h-96 object-cover"
                  />
                </div>
                {product.images.length > 1 && (
                  <div className="grid grid-cols-4 gap-4">
                    {product.images.map((image, index) => (
                      <div 
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        data-testid={`thumbnail-${index}`}
                        className={`border cursor-pointer overflow-hidden transition-all ${
                          selectedImage === index ? 'border-[#FF4500]' : 'border-white/10 hover:border-white/30'
                        }`}
                      >
                        <img src={image} alt={`${product.name} ${index + 1}`} className="w-full h-20 object-cover" />
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="border border-white/10 h-96 flex items-center justify-center">
                <ShoppingCart size={64} className="text-white/20" />
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <h1 className="text-4xl md:text-5xl font-black mb-4" data-testid="product-title">{product.name}</h1>
            <p className="text-3xl font-bold text-[#FF4500] mb-6" data-testid="product-price">R{product.price.toFixed(2)}</p>
            
            <div className="mb-6 pb-6 border-b border-white/10">
              <p className="text-white/70 leading-relaxed" data-testid="product-description">{product.description}</p>
            </div>

            <div className="mb-6">
              <p className="text-sm text-white/50 mb-2">Availability</p>
              {product.inventory > 0 ? (
                <p className="text-green-400 font-bold" data-testid="stock-status">
                  In Stock ({product.inventory} available)
                </p>
              ) : (
                <p className="text-red-400 font-bold" data-testid="out-of-stock-status">Out of Stock</p>
              )}
            </div>

            {product.inventory > 0 && (
              <>
                <div className="flex items-center gap-4 mb-6">
                  <p className="text-sm text-white/50">Quantity</p>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      data-testid="decrease-quantity"
                      size="icon"
                      variant="outline"
                      className="border-white/20 hover:bg-white/10"
                    >
                      <Minus size={16} />
                    </Button>
                    <span className="w-12 text-center font-bold" data-testid="quantity-display">{quantity}</span>
                    <Button
                      onClick={() => setQuantity(Math.min(product.inventory, quantity + 1))}
                      data-testid="increase-quantity"
                      size="icon"
                      variant="outline"
                      className="border-white/20 hover:bg-white/10"
                    >
                      <Plus size={16} />
                    </Button>
                  </div>
                </div>

                <Button
                  onClick={addToCart}
                  data-testid="add-to-cart-button"
                  className="w-full bg-[#FF4500] hover:bg-[#FF4500]/90 text-white uppercase tracking-widest font-bold px-8 py-6 text-lg neon-glow-hover"
                >
                  <ShoppingCart className="mr-2" size={20} />
                  Add to Cart
                </Button>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ProductDetail;